import type { ReactElement } from 'react';

import { CatalogSelect } from '@/components/ui/CatalogSelect';
import { clausesOf, normCodesOf } from '@/lib/quality-catalogs';

import type { ExtraFilter, UiFilters } from '../use-url-filters';
import type { FilterCatalogs } from './FilterBar';

// Fila de "+ Filtro" de la Lista Maestra (so=430; mockup 02): norma y cláusula para
// la auditoría por requisito, tipo de documento y proceso (con sus subprocesos). Cada
// cambio se aplica en la URL; la cláusula espera a que haya una norma.

interface Props {
  filters: UiFilters;
  catalogs: FilterCatalogs;
  onSetFilters: (next: Partial<Record<ExtraFilter, string | null>>) => void;
}

export function MoreFilters({ filters, catalogs, onSetFilters }: Props): ReactElement {
  // Se ofrecen también las normas y cláusulas desactivadas: lo que ya estaba
  // clasificado en ellas tiene que seguir encontrándose en una auditoría.
  const all = { includeInactive: true };
  const catalog = catalogs.norms;
  const norms = catalog
    ? normCodesOf(catalog, all).map((code) => ({
        id: code,
        name: code,
        isActive: catalog.some((n) => n.normCode === code && n.isActive),
      }))
    : undefined;
  const clauses =
    filters.norm !== null && catalog
      ? clausesOf(catalog, filters.norm, all).map((c) => ({
          id: c.clause,
          name: `${c.clause} · ${c.title}`,
          isActive: c.isActive,
        }))
      : undefined;
  return (
    <div className="filters-more" role="group" aria-label="Más filtros">
      <CatalogSelect
        label="Norma"
        allLabel="Todas"
        inactiveSuffix=" (desactivada)"
        failed={catalogs.failed.norms}
        onRetry={() => catalogs.retry('norms')}
        value={filters.norm}
        items={norms}
        onChange={(v) => onSetFilters({ norm: v })}
      />
      <CatalogSelect
        label="Cláusula"
        allLabel="Todas"
        inactiveSuffix=" (desactivada)"
        value={filters.clause}
        items={clauses}
        onChange={(v) => onSetFilters({ clause: v })}
      />
      <CatalogSelect
        label="Tipo de documento"
        failed={catalogs.failed.types}
        onRetry={() => catalogs.retry('types')}
        value={filters.type}
        items={catalogs.types}
        onChange={(v) => onSetFilters({ type: v })}
      />
      <CatalogSelect
        label="Proceso"
        failed={catalogs.failed.areas}
        onRetry={() => catalogs.retry('areas')}
        value={filters.area}
        items={catalogs.areas}
        onChange={(v) => onSetFilters({ area: v })}
      />
    </div>
  );
}
